/** IPCC 100-year global warming potentials (GWP100). */
export type GwpReport = 'AR4' | 'AR5' | 'AR6';

export type GreenhouseGas = {
  id: string;
  name: string;
  formula: string;
  /** GWP100 by assessment report. Missing reports fall back to the nearest published value. */
  gwp: Partial<Record<GwpReport, number>>;
};

export const GWP_REPORTS: { id: GwpReport; label: string; source: string }[] = [
  { id: 'AR6', label: 'IPCC AR6 (2021)', source: 'IPCC Sixth Assessment Report, 100-year GWP' },
  { id: 'AR5', label: 'IPCC AR5 (2013)', source: 'IPCC Fifth Assessment Report, 100-year GWP' },
  { id: 'AR4', label: 'IPCC AR4 (2007)', source: 'IPCC Fourth Assessment Report, 100-year GWP' },
];

export const GREENHOUSE_GASES: GreenhouseGas[] = [
  { id: 'CO2', name: 'Carbon dioxide', formula: 'CO₂', gwp: { AR4: 1, AR5: 1, AR6: 1 } },
  { id: 'CH4', name: 'Methane (fossil)', formula: 'CH₄', gwp: { AR4: 25, AR5: 28, AR6: 29.8 } },
  { id: 'CH4_BIO', name: 'Methane (biogenic)', formula: 'CH₄', gwp: { AR4: 25, AR5: 28, AR6: 27.2 } },
  { id: 'N2O', name: 'Nitrous oxide', formula: 'N₂O', gwp: { AR4: 298, AR5: 265, AR6: 273 } },
  { id: 'HFC134A', name: 'HFC-134a', formula: 'CH₂FCF₃', gwp: { AR4: 1430, AR5: 1300, AR6: 1530 } },
  { id: 'HFC32', name: 'HFC-32', formula: 'CH₂F₂', gwp: { AR4: 675, AR5: 677, AR6: 771 } },
  { id: 'HFC23', name: 'HFC-23', formula: 'CHF₃', gwp: { AR4: 14800, AR5: 12400, AR6: 14600 } },
  { id: 'CF4', name: 'PFC-14 (tetrafluoromethane)', formula: 'CF₄', gwp: { AR4: 7390, AR5: 6630, AR6: 7380 } },
  { id: 'C2F6', name: 'PFC-116', formula: 'C₂F₆', gwp: { AR4: 12200, AR5: 11100, AR6: 12400 } },
  { id: 'SF6', name: 'Sulphur hexafluoride', formula: 'SF₆', gwp: { AR4: 22800, AR5: 23500, AR6: 25200 } },
  { id: 'NF3', name: 'Nitrogen trifluoride', formula: 'NF₃', gwp: { AR4: 17200, AR5: 16100, AR6: 17400 } },
];

export const MASS_UNITS = [
  { id: 'kg', label: 'kilograms (kg)', toKg: 1 },
  { id: 't', label: 'metric tonnes (t)', toKg: 1000 },
  { id: 'g', label: 'grams (g)', toKg: 0.001 },
  { id: 'lb', label: 'pounds (lb)', toKg: 0.45359237 },
] as const;

export type MassUnit = (typeof MASS_UNITS)[number]['id'];

export function gwpFor(gas: GreenhouseGas, report: GwpReport): number {
  return gas.gwp[report] ?? gas.gwp.AR6 ?? gas.gwp.AR5 ?? gas.gwp.AR4 ?? 1;
}

export function toKg(amount: number, unit: MassUnit): number {
  const found = MASS_UNITS.find((item) => item.id === unit);
  return amount * (found?.toKg ?? 1);
}

export function fromKg(kg: number, unit: MassUnit): number {
  const found = MASS_UNITS.find((item) => item.id === unit);
  return kg / (found?.toKg ?? 1);
}

export function formatMass(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const digits = abs >= 100 ? 2 : abs >= 1 ? 4 : 6;
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
}
